import { toPairs } from 'lodash';

import { PopoverRootProps } from 'ui/Popover';
import { updateChart } from 'utils/form';
import {
  sankeyFormFlowsMapToArray,
  sankeyFormNodesArrayToMap,
  sankeyFormNodesMapToArray,
} from 'utils/sankey';

import NodeMoves from '../../diagram/nodeMoves';
import { ep } from '../../diagram/numbers';
import { SankeyFlow } from '../../diagram/types';
import { TeamSettings } from '../../utils/team';
import { patchNodesWithNewFlows } from '../DataEditor/utils';

export const buildFlowPath = (f: SankeyFlow) => {
  const syC = (f.source.y ?? NaN) + (f.sy ?? NaN) + (f.dy ?? NaN) / 2; // source flow's y center
  const tyC = (f.target.y ?? NaN) + (f.ty ?? NaN) + (f.dy ?? NaN) / 2; // target flow's y center

  const sx = (f.source.x ?? NaN) + (f.source.dx ?? NaN); // source's trailing edge
  const tx = f.target.x ?? NaN; // target's leading edge

  const syTop = (f.source.y ?? NaN) + (f.sy ?? NaN); // source flow top
  const tyBot = (f.target.y ?? NaN) + (f.ty ?? NaN) + (f.dy ?? NaN); // target flow bottom

  // Take center point of the flow's source and target x's which will form 2 Bezier curve control points.
  // This is a simplified formula of a previous interpolation formula under the assumption curvature=0.5
  // TODO: Improve this algorithm overall as a part of https://github.com/sankeyart/sankeyart/issues/162
  const xcp = (sx + tx) / 2;

  // Very straight paths (total rise/fall < 1 pixel) cause weird artifacts
  // in some SVG renderers so render them as flat paths instead.
  const isFlat = Math.abs(syC - tyC) < 1;

  return {
    isFlat,
    d: isFlat
      ? // For a flat path, we just draw a parallelogram:
        // [M]ove to the flow source's top; draw a [v]ertical line down,
        // a [L]ine to the opposite corner, a [v]ertical line up,
        // then [z] close.
        `M${ep(sx)} ${ep(syTop)}v${ep(f.dy)}` +
        `L${ep(tx)} ${ep(tyBot)}v-${ep(f.dy)}z`
      : // For a curved path, we draw a Bezier curve:
        // [M]ove to the center of the flow's start [sx,syC]
        // Draw a Bezier [C]urve using control points [xcp,syC] & [xcp,tyC]
        // End at the center of the flow's target [tx,tyC]
        `M${ep(sx)} ${ep(syC)}` +
        `C${ep(xcp)} ${ep(syC)}` +
        ` ${ep(xcp)} ${ep(tyC)}` +
        ` ${ep(tx)} ${ep(tyC)}`,
  };
};

// Use this function to center the popover's anchor in the middle of the reference element
export const centerOverTrigger: PopoverRootProps['positioning'] = {
  getAnchorRect: (element) => {
    if (!element) {
      return null;
    }
    const rect = element.getBoundingClientRect();
    return {
      x: rect.x + rect.width / 2,
      y: rect.y + rect.height / 2,
      width: 0,
      height: 0,
    };
  },
};

export const useIsLeafNode = (form: SankeyForm, nodeName: string) => {
  const flowsArray = sankeyFormFlowsMapToArray(form.watch('flows'));
  const flowsForNode = flowsArray.filter(
    (flow) => flow.source === nodeName || flow.target === nodeName
  );
  return flowsForNode.length === 1;
};

export const useIsPeriferalFlow = (form: SankeyForm, flowId: string) => {
  const sourceNodeName = form.watch(`flows.${flowId}.source`);
  const targetNodeName = form.watch(`flows.${flowId}.target`);
  const isLeafSourceNode = useIsLeafNode(form, sourceNodeName);
  const isLeafTargetNode = useIsLeafNode(form, targetNodeName);
  return isLeafSourceNode || isLeafTargetNode;
};

export const useHasDiagramMultipleFlows = (form: SankeyForm) => {
  const flowsArray = sankeyFormFlowsMapToArray(form.watch('flows'));
  const validFlows = flowsArray.filter((flow) => flow.source && flow.target);
  return validFlows.length > 1;
};

export const getNodeByName = (form: SankeyForm, nodeName: string) => {
  return toPairs(form.getValues('nodes'))?.find(
    ([_, node]) => node.name === nodeName
  )?.[1];
};

// TODO: Unit test this method
export function updateNodesFromFlows(
  form: SankeyForm,
  teamSettings: TeamSettings
) {
  const renameNode = (oldName: string, newName: string) => {
    new NodeMoves(form).renameNode(oldName, newName);
  };
  form.setValue(
    'nodes',
    sankeyFormNodesArrayToMap(
      patchNodesWithNewFlows(
        sankeyFormNodesMapToArray(form.getValues('nodes')),
        sankeyFormFlowsMapToArray(form.getValues('flows')),
        renameNode,
        teamSettings
      )
    )
  );
  updateChart(form);
}

export const popoverNoAnimation = {
  exit: {},
  enter: {},
};
